import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AuthStateResponse, SsoDiscoverResponse, SsoStartResponse } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorLine } from '../../components/ErrorPanel';
import { Alert, Button, TextField } from '../../components/ui';
import { goTo } from '../../modules/security/redirect';
import { AuthLayout, useProceed } from './AuthLayout';

const SSO_ERRORS = ['failed', 'cancelled', 'expired', 'email_missing', 'not_allowed', 'unavailable'] as const;
type SsoError = (typeof SSO_ERRORS)[number];
const LOOKS_LIKE_EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Microsoft sign-in: offered when the typed email (or the organisation's own address) says it is available. */
function useSso(email: string, tenantSlug: string | undefined) {
  const [probe, setProbe] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setProbe(LOOKS_LIKE_EMAIL.test(email) ? email : ''), 400);
    return () => clearTimeout(id);
  }, [email]);
  const discover = useQuery({
    queryKey: ['sso-discover', probe, tenantSlug ?? null],
    queryFn: () => api.get('/auth/sso/discover', { query: { email: probe || undefined, tenantSlug }, schema: SsoDiscoverResponse }),
    enabled: probe !== '' || !!tenantSlug,
    retry: false,
  });
  const start = useMutation({
    mutationFn: () =>
      api.post('/auth/sso/start', {
        body: { ...(LOOKS_LIKE_EMAIL.test(email) ? { email } : {}), ...(tenantSlug ? { tenantSlug } : {}) },
        schema: SsoStartResponse,
      }),
    onSuccess: (r) => goTo(r.url),
  });
  return { available: discover.data?.available ?? false, required: discover.data?.required ?? false, start };
}

export function LoginPage() {
  const [params] = useSearchParams();
  const proceed = useProceed();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const tenantSlug = params.get('tenant') ?? undefined;
  const sso = useSso(email, tenantSlug);
  const ssoCode = params.get('sso_error');
  const ssoError: SsoError | null = ssoCode ? ((SSO_ERRORS as readonly string[]).includes(ssoCode) ? (ssoCode as SsoError) : 'failed') : null;
  const login = useMutation({
    mutationFn: () =>
      api.post('/auth/login', { body: { email, password, ...(tenantSlug ? { tenantSlug } : {}) }, schema: AuthStateResponse }),
    onSuccess: proceed,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };
  return (
    <AuthLayout title={t('auth.signIn')}>
      <form onSubmit={submit} className="space-y-4">
        {ssoError ? <Alert>{t(`auth.sso.error.${ssoError}`)}</Alert> : null}
        <ErrorLine error={login.error ?? sso.start.error} />
        <TextField label={t('auth.email')} type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <TextField
          label={t('auth.password')}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button type="submit" variant={sso.required ? 'secondary' : 'primary'} className="w-full" disabled={login.isPending}>
          {t('auth.signIn')}
        </Button>
        {sso.available ? (
          <>
            <p className="text-center text-sm text-slate-600">{t('auth.sso.or')}</p>
            <Button variant={sso.required ? 'primary' : 'secondary'} className="w-full" disabled={sso.start.isPending} onClick={() => sso.start.mutate()}>
              {t('auth.sso.button')}
            </Button>
          </>
        ) : null}
        <p className="text-center text-sm">
          <Link className="text-brand underline" to="/forgot-password">
            {t('auth.forgotPassword')}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
